import { SxProps, Theme } from '@mui/material/styles';

/**
 * Design Tokens - ClearChoice 2.0 Header
 * Extracted from Figma: https://www.figma.com/design/qaGraJTHoBVvdcXs65AWLe/ClearChoice-2.0-Alpha
 */

// Schedule CTA palette — DCP-14444: rolled out blue schedule/call-us buttons across brands.
// Mirrors clearchoice-marketing (feat/DCP-14444/roll-out-schedule-cta-blue-color).
const SCHEDULE_CTA_COLORS = {
	base: '#276AFF',
	hover: '#2056CE',
	active: '#1843A0',
	text: '#FFFFFF',
};

// Color Palette
const COLORS = {
	primary: '#F77F00', // ClearChoice orange button
	primaryHover: '#E67E00', // Darker orange on hover
	callUs: SCHEDULE_CTA_COLORS.base, // DCP-14444: Call us button background — blue
	callUsHover: SCHEDULE_CTA_COLORS.hover, // DCP-14444: Call us button hover — blue
	callUsActive: SCHEDULE_CTA_COLORS.active, // DCP-14444: Call us button active/pressed — blue
	scheduleNow: SCHEDULE_CTA_COLORS.base, // DCP-14444: Schedule Now button — blue pill
	scheduleNowHover: SCHEDULE_CTA_COLORS.hover, // DCP-14444: Schedule Now button hover — blue
	scheduleNowActive: SCHEDULE_CTA_COLORS.active, // DCP-14444: Schedule Now button active/pressed — blue
	scheduleNowText: SCHEDULE_CTA_COLORS.text, // DCP-14444: Schedule/Call us button text — white
	navy: '#0A2F54', // Figma: Brand navy – logo tint, icon color, call us text
	iconBorder: '#A8B1BA', // Figma: Neutrals-500 – icon button border
	iconBg: 'rgba(253, 253, 253, 0.50)', // Figma: Icon button background
	text: {
		primary: '#1A1A1A', // Dark text
		secondary: '#666666', // Secondary text
		light: '#999999', // Light text for icons
	},
	background: {
		light: '#FFFFFF', // White background
		lighter: '#F5F5F5', // Light gray for tiles
		overlay: '#F0F0F0', // Light overlay
	},
	border: {
		light: '#E0E0E0', // Light borders
		lighter: '#F0F0F0', // Lighter borders
		divider: '#C7D8EA', // Figma: pill outline + nav dividers
		topBar: '#AFAD98', // Figma: mobile top bar bottom border
	},
	tile: {
		bg: '#EEF3F9', // Figma: content tile card background
		bgHover: '#DDE9F3', // Figma: content tile card hover background
		text: '#4B493E', // Figma: content tile title / description text
	},
	shadow: 'rgba(0, 0, 0, 0.12)', // Box shadows
};

// Typography
const TYPOGRAPHY = {
	fontFamily: '"Beausite Classic", "Beausite", sans-serif',
	nav: {
		fontSize: '14px',
		fontWeight: 500,
		lineHeight: '1.4',
	},
	navLarge: {
		fontSize: '15px',
		fontWeight: 500,
		lineHeight: '1.4',
	},
	button: {
		fontSize: '14px',
		fontWeight: 600,
		lineHeight: '1.2',
	},
	buttonSmall: {
		fontSize: '12px',
		fontWeight: 600,
		lineHeight: '1.2',
	},
	title: {
		fontSize: '16px',
		fontWeight: 600,
		lineHeight: '1.3',
	},
	tileTitle: {
		fontSize: '13px',
		fontWeight: 600,
		lineHeight: '1.3',
	},
	tileDescription: {
		fontSize: '12px',
		fontWeight: 400,
		lineHeight: '1.3',
	},
};

// Spacing
const SPACING = {
	headerHeight: '64px',
	headerTopRowHeight: '48px',
	headerBottomRowHeight: '48px',
	headerTotalHeight: '96px',
	headerPaddingDesktop: { xs: '16px', sm: '24px', md: '32px', lg: '40px' },
	navGap: '24px',
	contentGap: '16px',
	tileGap: '8px',
	buttonPadding: '8px 16px',
	buttonSmallPadding: '6px 12px',
	drawerPadding: '16px',
};

// Breakpoints
const BREAKPOINTS = {
	mobile: 600,
	tablet: 960,
	desktop: 1280,
};

export const headerStyles = {
	headerAppBar: {
		backgroundColor: 'transparent',
		boxShadow: 'none',
		position: 'fixed' as const,
		top: 0,
		zIndex: 1100,
		overflow: 'visible',
		fontFamily: TYPOGRAPHY.fontFamily,
		'--header-height': SPACING.headerTotalHeight,
	} as SxProps<Theme>,

	// Full-width stacked navigation bar — replaces floating pill design
	// Positioned relative so absolute mega-menu panels resolve correctly
	desktopHeaderContainer: {
		position: 'relative' as const,
		backgroundColor: 'rgba(255, 255, 255, 0.92)',
		backdropFilter: 'blur(8px)',
		boxShadow: '0px 4px 16px 0px rgba(38, 32, 74, 0.12)',
		borderBottom: `1px solid ${COLORS.border.divider}`,
		display: 'flex',
		flexDirection: 'column' as const,
		justifyContent: 'center',
		alignItems: 'stretch',
		gap: '12px',
		padding: { md: '16px 20px 12px 20px', lg: '16px 40px 12px 40px' },
		// overflow: visible (default) so the mega-menu panels — which are
		// position:absolute children — can render below without being clipped.
	} as SxProps<Theme>,

	desktopTopRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		width: '100%',
		paddingBottom: '12px',
	} as SxProps<Theme>,

	desktopBottomRow: {
		display: 'flex',
		alignItems: 'stretch',
		justifyContent: 'space-between',
		width: '100%',
	} as SxProps<Theme>,

	desktopLogo: {
		height: '48px',
		width: '164px',
		aspectRatio: '41 / 12',
		display: 'flex',
		alignItems: 'center',
		'& img': {
			height: '100%',
			width: '100%',
			objectFit: 'contain',
		},
	} as SxProps<Theme>,

	desktopActions: {
		display: { xs: 'none', md: 'flex' },
		alignItems: 'center',
		gap: { md: '8px', lg: '16px', xl: '20px' },
		ml: 'auto',
	} as SxProps<Theme>,

	// ============================================================
	// Desktop navigation item styles
	// ============================================================

	navItem: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		alignSelf: 'stretch',
		fontSize: { md: '14px', lg: '16px' },
		fontWeight: 350,
		lineHeight: '28px',
		fontFamily: TYPOGRAPHY.fontFamily,
		letterSpacing: 0,
		color: COLORS.navy,
		cursor: 'pointer',
		flexShrink: 0,
		height: '100%',
		px: { md: '4px', lg: '16px', xl: '24px' },
	} as SxProps<Theme>,

	// Button-specific overrides for nav items that open a mega-menu.
	// Resets browser button chrome and adds a visible keyboard focus ring.
	navItemButton: {
		background: 'none',
		border: 'none',
		'&:focus-visible': {
			outline: `2px solid ${COLORS.navy}`,
			outlineOffset: '2px',
			borderRadius: '2px',
		},
	} as SxProps<Theme>,

	// Inner span — border only covers text+arrow, not full button width
	navItemInner: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: '4px',
		borderBottom: '1px solid transparent',
		transition: 'border-color 0.15s ease',
		'&:hover': {
			borderBottomColor: COLORS.navy,
		},
	} as SxProps<Theme>,

	navItemActive: {
		color: COLORS.navy,
		borderBottomColor: COLORS.navy,
		borderBottomWidth: '1px',
	} as SxProps<Theme>,

	// ============================================================
	// Desktop utility actions (Find a center, phone, Schedule now)
	// ============================================================

	// Find a center — no background, navy text, 4px gap, pill padding
	desktopActionLink: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '4px',
		padding: '10px 8px 10px 4px',
		borderRadius: '20px',
		color: COLORS.navy,
		textDecoration: 'none',
		cursor: 'pointer',
		'&:hover': { opacity: 0.75 },
	} as SxProps<Theme>,

	desktopActionLinkText: {
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '16px',
		fontWeight: 400,
		lineHeight: '20px',
		whiteSpace: 'nowrap' as const,
		color: COLORS.navy,
	} as SxProps<Theme>,

	desktopUtilityIcon: {
		fontSize: '20px',
		width: '20px',
		height: '20px',
		flexShrink: 0,
		color: 'inherit',
	} as SxProps<Theme>,

	// Phone — display only, not a link
	desktopPhonePill: {
		display: 'flex',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '4px',
		padding: '10px 8px 10px 4px',
		borderRadius: '20px',
		backgroundColor: 'transparent',
		textDecoration: 'none',
		color: COLORS.navy,
	} as SxProps<Theme>,

	desktopPhonePillText: {
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '16px',
		fontWeight: 400,
		lineHeight: '20px',
		whiteSpace: 'nowrap' as const,
		color: COLORS.navy,
	} as SxProps<Theme>,

	scheduleButton: {
		display: 'flex',
		padding: '10px 20px',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '10px',
		borderRadius: '20px',
		backgroundColor: COLORS.scheduleNow,
		color: COLORS.scheduleNowText,
		textAlign: 'center',
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '16px',
		fontStyle: 'normal',
		fontWeight: 400,
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		textTransform: 'none',
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: COLORS.scheduleNowHover,
		},
		'&:active': {
			backgroundColor: COLORS.scheduleNowActive,
		},
		'&:focus-visible': {
			outline: `2px solid ${COLORS.navy}`,
			outlineOffset: '2px',
		},
	} as SxProps<Theme>,

	// ============================================================
	// Desktop mega-menu styles
	// ============================================================

	// "Ghost" Schedule now — TTB-322 `ghostPrimaryScheduleButton` variation.
	// Layout/geometry only (matches scheduleButton) so the MUI `tertiaryDefault`
	// theme variant supplies the lower-emphasis colors and the CTA reads like
	// its neighboring utility actions instead of the solid blue pill.
	scheduleButtonGhost: {
		display: 'flex',
		padding: '10px 20px',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '10px',
		borderRadius: '20px',
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '16px',
		fontStyle: 'normal',
		fontWeight: 400,
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		textTransform: 'none',
		textDecoration: 'none',
	} as SxProps<Theme>,

	megaMenuWrapper: {
		// position: absolute relative to desktopHeaderContainer (nearest positioned ancestor).
		// top: 100% places it flush below the pill. left:0 / right:0 guarantee the
		// mega-menu width is identical to the container — no JS measurement needed.
		position: 'absolute' as const,
		top: '100%',
		left: 0,
		right: 0,
		zIndex: 10,
	} as SxProps<Theme>,

	megaMenuPanel: {
		background: '#FFF',
		boxShadow: '0 6px 16px 0 rgba(38, 32, 74, 0.25)',
		// Responsive padding: tighter at tablet (md), full at lg+
		padding: { md: '20px 40px 24px 40px', lg: '28px 100px 32px 100px' },
		display: 'flex',
		flexDirection: 'column' as const,
		justifyContent: 'center',
		alignItems: 'stretch',
		gap: '12px',
		borderRadius: '0 0 20px 20px',
		borderBottom: `1px solid ${COLORS.border.divider}`,
		width: '100%',
		flexShrink: 0,
	} as SxProps<Theme>,

	megaMenuColumnsContainer: {
		display: 'flex',
		width: '100%',
		justifyContent: 'flex-start',
		flexDirection: 'row' as const,
		alignItems: 'flex-start',
		gap: '20px',
	} as SxProps<Theme>,

	// One of the three columns (col1, col2, col3) — equal flex distribution
	megaMenuColumn: {
		flex: 1,
		paddingTop: '20px',
		display: 'flex',
		flexDirection: 'column' as const,
		gap: '20px',
		alignItems: 'flex-start',
	} as SxProps<Theme>,

	// Category heading — Beausite Classic, 14px Body Small Book
	megaMenuGroupTitle: {
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '14px',
		fontWeight: 350,
		color: COLORS.navy,
		lineHeight: '24px',
		letterSpacing: 0,
		alignSelf: 'stretch',
		padding: 0,
		cursor: 'default',
		width: '100%',
	} as SxProps<Theme>,

	// Standalone group link (no sub-links) — Beausite Classic, 18px semibold
	megaMenuGroupLink: {
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '18px',
		fontWeight: 500,
		color: COLORS.navy,
		lineHeight: '28px',
		padding: 0,
		cursor: 'pointer',
		textDecoration: 'none',
		display: 'block',
		'&:hover': {
			opacity: 0.7,
		},
	} as SxProps<Theme>,

	// Sub-link — Beausite Classic, 18px, weight 500 (Semibold)
	megaMenuSubLink: {
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '18px',
		fontWeight: 500,
		color: COLORS.navy,
		lineHeight: '28px',
		letterSpacing: 0,
		alignSelf: 'stretch',
		padding: 0,
		cursor: 'pointer',
		textDecoration: 'none',
		display: 'block',
		'&:hover': {
			opacity: 0.7,
		},
	} as SxProps<Theme>,

	// 3rd column — content tiles, equal flex distribution with columns 1 & 2
	// minWidth ensures the tile card (104px image + text) never gets too narrow on tablet
	megaMenuTilesSection: {
		flex: 1,
		minWidth: { md: '350px', lg: 'unset' },
		display: 'flex',
		flexDirection: 'column' as const,
		gap: '8px',
		alignSelf: 'flex-start',
	} as SxProps<Theme>,

	// ============================================================
	// Content tile card styles (shared by desktop + mobile)
	// ============================================================

	contentTileCard: {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
		height: '104px',
		paddingRight: '12px',
		gap: '10px',
		borderRadius: '16px',
		backgroundColor: COLORS.tile.bg,
		cursor: 'pointer',
		textDecoration: 'none',
		boxSizing: 'border-box' as const,
		'&:hover': {
			backgroundColor: COLORS.tile.bgHover,
		},
	} as SxProps<Theme>,

	contentTileCardImage: {
		width: '104px',
		height: '104px',
		aspectRatio: '1/1',
		borderRadius: '16px 0 0 16px',
		backgroundSize: '151.645% 101.034%',
		backgroundPosition: '-23.608px -0.501px',
		backgroundRepeat: 'no-repeat',
		backgroundBlendMode: 'multiply, normal',
		flexShrink: 0,
	} as SxProps<Theme>,

	contentTileCardText: {
		flex: 1,
		display: 'flex',
		flexDirection: 'column' as const,
		gap: '4px',
		minWidth: 0,
	} as SxProps<Theme>,

	contentTileCardTitle: {
		fontFamily: 'var(--Brand-Typography-Body-Medium---Bold-Typeface, "Beausite Classic") !important',
		fontSize: '16px',
		fontStyle: 'normal',
		fontWeight: '700 !important',
		color: COLORS.tile.text,
		lineHeight: '28px',
		letterSpacing: '0',
		alignSelf: 'stretch',
	} as SxProps<Theme>,

	contentTileCardDescription: {
		fontFamily: '"Beausite Classic" !important',
		fontSize: '14px',
		fontStyle: 'normal',
		fontWeight: '350 !important',
		color: COLORS.tile.text,
		lineHeight: '24px',
		letterSpacing: '0',
		alignSelf: 'stretch',
	} as SxProps<Theme>,

	contentTileCardChevron: {
		color: COLORS.navy,
		fontSize: '20px',
		flexShrink: 0,
		opacity: 0.5,
	} as SxProps<Theme>,

	// ============================================================
	// Mobile top bar
	// ============================================================

	mobileTopBar: {
		display: 'flex',
		padding: '12px 20px 15px 20px',
		justifyContent: 'space-between',
		alignItems: 'center',
		position: 'relative' as const,
		borderBottom: `1px solid ${COLORS.border.topBar}`,
		backgroundColor: 'rgba(253, 253, 253, 0.60)',
		backdropFilter: 'blur(8px)',
	} as SxProps<Theme>,

	mobileIconButton: {
		display: 'flex',
		width: '36px',
		height: '36px',
		justifyContent: 'center',
		alignItems: 'center',
		borderRadius: '47.366px',
		border: `0.877px solid ${COLORS.iconBorder}`,
		background: COLORS.iconBg,
		color: COLORS.navy,
		padding: 0,
		flexShrink: 0,
		'&:hover': {
			background: 'rgba(10, 47, 84, 0.08)',
		},
		'& .MuiSvgIcon-root': {
			fontSize: '20px',
			color: COLORS.navy,
		},
	} as SxProps<Theme>,

	mobileLogoBox: {
		width: '101px',
		height: '32px',
		display: 'flex',
		alignItems: 'center',
		flexShrink: 0,
	} as SxProps<Theme>,

	// "Call us" pill button in the mobile top bar — same Figma spec as scheduleButton
	mobileCallUsButton: {
		display: 'flex',
		padding: '10px 20px',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '10px',
		borderRadius: '20px',
		backgroundColor: COLORS.callUs,
		color: COLORS.scheduleNowText,
		textAlign: 'center',
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '16px',
		fontStyle: 'normal',
		fontWeight: 400,
		lineHeight: '20px',
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: COLORS.callUsHover,
		},
		'&:active': {
			backgroundColor: COLORS.callUsActive,
		},
		'&:focus-visible': {
			outline: `2px solid ${COLORS.navy}`,
			outlineOffset: '2px',
		},
	} as SxProps<Theme>,

	// ============================================================
	// Mobile navigation drawer
	// ============================================================

	// "Ghost" Call us — TTB-322 `ghostPrimaryScheduleButton` variation.
	// Layout/geometry only (matches mobileCallUsButton) so the MUI `secondaryDefault`
	// theme variant supplies the outlined lower-emphasis look, matching the
	// bordered "Find a center" icon button next to it instead of the solid blue pill.
	mobileCallUsButtonGhost: {
		display: 'flex',
		padding: '10px 20px',
		justifyContent: 'center',
		alignItems: 'center',
		gap: '10px',
		borderRadius: '20px',
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '16px',
		fontStyle: 'normal',
		fontWeight: 400,
		lineHeight: '20px',
		whiteSpace: 'nowrap',
		textTransform: 'none',
		textDecoration: 'none',
	} as SxProps<Theme>,

	mobileDrawerPaper: {
		display: 'flex',
		width: '100%',
		height: '100%',
		borderRadius: 0,
		boxShadow: 'none',
		padding: '20px 0 32px 0',
		flexDirection: 'column' as const,
		alignItems: 'stretch',
		gap: '28px',
		backgroundColor: COLORS.background.light,
	} as SxProps<Theme>,

	mobileDrawerHeaderRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-end',
		alignSelf: 'stretch',
		padding: '0 20px',
	} as SxProps<Theme>,

	// Close button inside the drawer header row
	mobileDrawerCloseButton: {
		color: COLORS.navy,
		width: '28px',
		height: '28px',
		padding: 0,
	} as SxProps<Theme>,

	// Main menu — top-level nav items
	mobileNavItem: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingTop: '24px',
		paddingBottom: '24px',
		alignSelf: 'stretch',
		borderBottom: `1px solid ${COLORS.border.divider}`,
		cursor: 'pointer',
		textDecoration: 'none',
		'&:first-of-type': {
			borderTop: `1px solid ${COLORS.border.divider}`,
		},
	} as SxProps<Theme>,

	mobileNavItemText: {
		flex: '1 0 0',
		fontFamily: TYPOGRAPHY.fontFamily,
		fontSize: '22px',
		fontWeight: 500,
		color: COLORS.navy,
		lineHeight: '24px',
		letterSpacing: 0,
	} as SxProps<Theme>,

	// Chevron shown on nav items that have a sub-menu
	mobileNavItemChevron: {
		color: COLORS.navy,
		width: '16px',
		height: '16px',
	} as SxProps<Theme>,

	// Sub-menu — navigation groups
	mobileGroupContainer: {
		display: 'flex',
		flexDirection: 'column' as const,
		borderBottom: `1px solid ${COLORS.border.light}`,
	} as SxProps<Theme>,

	// Accordion toggle row for a group with sub-links
	mobileGroupHeader: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		pr: 2,
		py: 1.5,
		cursor: 'pointer',
		minHeight: '52px',
		'&:hover': {
			backgroundColor: COLORS.background.lighter,
		},
	} as SxProps<Theme>,

	mobileGroupHeaderText: {
		color: COLORS.navy,
		fontFamily: 'var(--Brand-Typography-Body-Large---Semibold-Typeface, "Beausite Classic") !important',
		fontSize: '18px !important',
		fontStyle: 'normal',
		fontWeight: '500 !important',
		lineHeight: '28px !important',
		letterSpacing: '0',
	} as SxProps<Theme>,

	// Expand / collapse chevron on group accordion headers
	mobileGroupToggleIcon: {
		color: COLORS.text.light,
		fontSize: '20px',
	} as SxProps<Theme>,

	mobileGroupSubItem: {
		display: 'flex',
		alignItems: 'center',
		alignSelf: 'stretch',
		padding: '20px 0',
		cursor: 'pointer',
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: COLORS.background.lighter,
		},
	} as SxProps<Theme>,

	mobileGroupSubItemText: {
		color: COLORS.navy,
		fontFamily: 'var(--Brand-Typography-Body-Large---Book-Typeface, "Beausite Classic") !important',
		fontSize: '18px !important',
		fontStyle: 'normal',
		fontWeight: '350 !important',
		lineHeight: '28px !important',
		letterSpacing: '0',
	} as SxProps<Theme>,

	mobileTilesSection: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: '16px',
		marginTop: '28px',
		alignSelf: 'stretch',
	} as SxProps<Theme>,

	// Sub-menu back navigation row
	mobileSubMenuBackRow: {
		display: 'flex',
		alignItems: 'center',
		gap: 1,
		pl: 0,
		pr: 2,
		pt: 0,
		pb: 0,
		marginBottom: '28px',
		cursor: 'pointer',
		minHeight: '48px',
		'&:hover': {
			backgroundColor: COLORS.background.lighter,
		},
	} as SxProps<Theme>,

	// Back arrow icon in the sub-menu back row
	mobileSubMenuBackIcon: {
		fontSize: '24px',
		color: COLORS.navy,
		width: '24px',
		height: '24px',
		flexShrink: 0,
		aspectRatio: '1/1',
	} as SxProps<Theme>,

	mobileSubMenuBackLabel: {
		fontSize: '12px',
		fontWeight: 700,
		fontFamily: TYPOGRAPHY.fontFamily,
		letterSpacing: '0.5px',
		textTransform: 'uppercase' as const,
		color: COLORS.navy,
		lineHeight: '20px',
	} as SxProps<Theme>,

	// Actions section (Find a center, Schedule now, Call us, quiz link)
	mobileDrawerActionsBottom: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: '20px',
		marginTop: '60px',
		backgroundColor: COLORS.background.light,
		flexShrink: 0,
	} as SxProps<Theme>,

	mobileActionItem: {
		display: 'flex',
		justifyContent: 'flex-start',
		alignItems: 'center',
		gap: '4px',
		padding: '10px 8px 10px 4px',
		borderRadius: '20px',
		color: COLORS.navy,
		cursor: 'pointer',
		textDecoration: 'none',
		'&:hover': {
			backgroundColor: COLORS.background.lighter,
		},
	} as SxProps<Theme>,

	mobileActionItemText: {
		fontSize: '16px',
		fontStyle: 'normal',
		fontWeight: 400,
		color: COLORS.navy,
		fontFamily: TYPOGRAPHY.fontFamily,
		lineHeight: '20px',
		letterSpacing: 0,
		textAlign: 'left',
	} as SxProps<Theme>,

	// Shared icon style for Find a center, Schedule now, and Call us action items
	mobileActionIcon: {
		fontSize: '20px',
		color: COLORS.navy,
	} as SxProps<Theme>,

	// CMS-supplied icons render as <img>, which ignores fontSize, so size them explicitly
	mobileActionIconImage: {
		width: '20px',
		height: '20px',
		flexShrink: 0,
		objectFit: 'contain',
	} as SxProps<Theme>,

	// ============================================================
	// Link style — removes underlines from all anchor items
	// ============================================================
	linkBase: {
		textDecoration: 'none',
	} as SxProps,
} as const;

/**
 * Design Token Configuration
 *
 * To customize colors and typography:
 * 1. Update COLORS object with your brand colors
 * 2. Update TYPOGRAPHY object with your font specifications
 * 3. Update SPACING object for layout adjustments
 *
 * All values are imported throughout the styles using the constants above.
 */
export const designTokens = {
	colors: COLORS,
	typography: TYPOGRAPHY,
	spacing: SPACING,
	breakpoints: BREAKPOINTS,
} as const;
